<template>
  <div class="field">
    <field-label v-if="label" v-bind="{ required, requiredClass, requiredText, hint, hintIcon, hintClass, description }">
      <slot/>
    </field-label>
    <p :class="['field-description', descriptionClass]" v-if="description">{{ description }}</p>
    <div class="field is-marginless" :class="{ 'has-addons': $slots.left || $slots.right }">
      <div class="control" v-if="$slots.left"><slot name="left"/></div>
      <div class="control is-expanded" :class="controlClass">
        <input
          :class="[{ [errorClass]: hasError }, classes]"
          @input="emit"
          @blur="emit"
          @focus="emit"
          v-bind="{ autocomplete, autofocus, dir, disabled, id, name, placeholder, readonly, required, role, type }"
          class="input"
          ref="control">
        <icon v-if="leftIcon" :class="['is-left', iconClass]" :pack="leftIconPack" :type="leftIconType" :icon="leftIcon"/>
        <icon v-if="rightIcon" :class="['is-right', iconClass]" :pack="rightIconPack" :type="rightIconType" :icon="rightIcon"/>
      </div>
      <div class="control" v-if="$slots.right"><slot name="right"/></div>
    </div>
    <field-error v-if="hasError" v-bind="{ error, errorClass, errorSymbol }"/>
  </div>
</template>

<script>
import * as props from '@/mixins/props'
import * as computed from '@/mixins/computed'
import Inputmask from 'inputmask/dist/inputmask'
import defaults from 'lodash/defaults'
import debounce from 'lodash/debounce'

export default {

  mixins: [
    props.autocomplete,
    props.autofocus,
    props.classes,
    props.debouncable,
    props.describable,
    props.dir,
    props.disabled,
    props.error,
    props.hintable,
    props.iconable,
    props.id,
    props.label,
    props.name,
    props.placeholder,
    props.readonly,
    props.required,
    props.role,
    props.working,

    computed.hasError,
  ],

  props: {
    value: {
      default: '',
    },
    type: {
      type: String,
      default: 'text',
    },
    mask: {
      type: String|Object,
      required: true,
    },
    options: {
      type: Object,
      default: () => ({}),
      placeholder: "\u2007",
      rightAlign: false,
    },
    raw: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      control: null,
      inputmask: null,
      emitDebounce: debounce(this.emitEvent, this.debounce, this.debounceOptions),
    }
  },

  computed: {
    controlClass() {
      return [{
        'has-icons-left': this.leftIcon,
        'has-icons-right': this.rightIcon,
        'is-loading': this.working
      }, this.classes]
    },
  },

  mounted() {
    this.control = this.$refs.control
    this.init()
  },

  watch: {
    value() {
      this.control.value = this.value
    },
    options: {
      deep: true,
      handler (options) {
        this.inputmask = this.mountInputmask()
      }
    },
  },

  methods: {
    mountInputmask() {
      return new Inputmask(
        this.mask,
        defaults(
          this.options,
          this.$vueBulma?.components.maskedTextInput.options
        )
      ).mask(this.control)
    },
    init() {
      this.control.value = this.value
      this.inputmask = this.mountInputmask()
      this.options.maxLength && this.control.setAttribute('maxlength', this.options.maxLength)
    },
    emittable() {
      return this.raw ? this.inputmask.unmaskedvalue() : this.control.value
    },
    emitEvent(event) {
      this.$emit(event.type, this.emittable())
    },
    emit(event) {
      if (typeof event === 'string') {
        event = { type: 'input' }
      }
      let method = this.debounce && event.type === 'input' ? 'emitDebounce' : 'emitEvent'
      this[method](event)
    },

  }

}
</script>
